import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.ts';
import './Register.css';
import { readRegistrationDraft, writeRegistrationDraft, clearRegistrationDraft } from '../utils/registrationDraftStorage.ts';
import { RegistrationDraftData } from '../utils/registrationDraftSchema.ts';
import { formatCnpj, isValidCnpj, normalizeCnpj } from '../utils/cnpj.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  ChevronRight,
  ChevronLeft,
  Bot,
  Check,
  Loader2,
  Upload,
  Image as ImageIcon,
  X,
  Users,
  Calendar,
  DollarSign,
  Globe,
  FileText,
  Info
} from 'lucide-react';

// Importação dos componentes de configuração
import AssistantIdentityForm from '../components/AssistantIdentityForm.tsx';
import TeamAndSpecialtiesForm from '../components/TeamAndSpecialtiesForm.tsx';
import SchedulingConfigForm from '../components/SchedulingConfigForm.tsx';
import FinancialConfigForm from '../components/FinancialConfigForm.tsx';

// Tipos
type Step = 1 | 2 | 3 | 4 | 5 | 6;

interface Treatment {
  treatmentTitle: string;
  description: string;
}

interface DayConfig {
  open: boolean;
  morningEnabled: boolean;
  morningStart: string;
  morningEnd: string;
  afternoonEnabled: boolean;
  afternoonStart: string;
  afternoonEnd: string;
}

const PHONE_QUERY_PARAMS = ['phone', 'telefone', 'whatsapp', 'admin_phone', 'lead_phone', 'slug'];
const REGISTRATION_POLICY_UNAVAILABLE_MESSAGE = 'O cadastro está temporariamente indisponível. Seus dados foram mantidos para você tentar novamente mais tarde.';

const PHONE_MIN_LOCAL_LENGTH = 10;
const PHONE_MAX_LOCAL_LENGTH = 11;
const PHONE_MIN_DDI_LENGTH = 12;
const PHONE_MAX_DDI_LENGTH = 13;

const REGISTRATION_PASSWORD_RANDOM_LENGTH = 12;
const REGISTRATION_PASSWORD_SUFFIX = '@sminy';
const PASSWORD_UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const PASSWORD_LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
const PASSWORD_DIGITS = '0123456789';
const PASSWORD_CHARACTERS = `${PASSWORD_UPPERCASE}${PASSWORD_LOWERCASE}${PASSWORD_DIGITS}`;
const UINT32_RANGE = 0x100000000;
const SECURE_PASSWORD_GENERATION_ERROR = 'Web Crypto API indisponível para gerar a senha';

type CryptoRandomSource = {
  getRandomValues: (values: Uint32Array) => Uint32Array;
};

const getSecureRandomIndex = (upperBound: number, randomSource: CryptoRandomSource) => {
  const randomValues = new Uint32Array(1);
  const maximumAcceptedValue = UINT32_RANGE - (UINT32_RANGE % upperBound);

  do {
    randomSource.getRandomValues(randomValues);
  } while (randomValues[0] >= maximumAcceptedValue);

  return randomValues[0] % upperBound;
};

const getSecureRandomCharacter = (characters: string, randomSource: CryptoRandomSource) => (
  characters[getSecureRandomIndex(characters.length, randomSource)]
);

export const generateRegistrationPassword = (
  randomSource: CryptoRandomSource = window.crypto,
) => {
  if (!randomSource || typeof randomSource.getRandomValues !== 'function') {
    throw new Error(SECURE_PASSWORD_GENERATION_ERROR);
  }

  try {
    const randomCharacters = [
      getSecureRandomCharacter(PASSWORD_UPPERCASE, randomSource),
      getSecureRandomCharacter(PASSWORD_LOWERCASE, randomSource),
      getSecureRandomCharacter(PASSWORD_DIGITS, randomSource),
    ];

    while (randomCharacters.length < REGISTRATION_PASSWORD_RANDOM_LENGTH) {
      randomCharacters.push(getSecureRandomCharacter(PASSWORD_CHARACTERS, randomSource));
    }

    for (let index = randomCharacters.length - 1; index > 0; index -= 1) {
      const swapIndex = getSecureRandomIndex(index + 1, randomSource);
      [randomCharacters[index], randomCharacters[swapIndex]] = [
        randomCharacters[swapIndex],
        randomCharacters[index],
      ];
    }

    return `${randomCharacters.join('')}${REGISTRATION_PASSWORD_SUFFIX}`;
  } catch {
    throw new Error(SECURE_PASSWORD_GENERATION_ERROR);
  }
};

const onlyDigits = (value: string) => value.replace(/\D/g, '');

const getPhoneInfo = (value: string) => {
  const digits = onlyDigits(value);
  const hasBrazilDdi = digits.startsWith('55') && digits.length > PHONE_MAX_LOCAL_LENGTH;
  const nationalDigits = hasBrazilDdi ? digits.slice(2) : digits;

  return {
    digits,
    hasBrazilDdi,
    nationalDigits,
    isValid:
      (nationalDigits.length === PHONE_MIN_LOCAL_LENGTH || nationalDigits.length === PHONE_MAX_LOCAL_LENGTH) &&
      (!hasBrazilDdi || digits.length === PHONE_MIN_DDI_LENGTH || digits.length === PHONE_MAX_DDI_LENGTH),
  };
};

const formatPhone = (value: string) => {
  const { hasBrazilDdi, nationalDigits } = getPhoneInfo(value);
  const limitedNational = nationalDigits.substring(0, PHONE_MAX_LOCAL_LENGTH);
  const prefix = hasBrazilDdi ? '+55 ' : '';

  if (!limitedNational) return hasBrazilDdi ? '+55 ' : '';

  if (limitedNational.length <= 2) {
    return `${prefix}(${limitedNational}`;
  }

  const areaCode = limitedNational.slice(0, 2);
  const subscriber = limitedNational.slice(2);
  const firstPartSize = subscriber.length > 8 ? 5 : 4;
  const firstPart = subscriber.slice(0, firstPartSize);
  const secondPart = subscriber.slice(firstPartSize, firstPartSize + 4);

  return `${prefix}(${areaCode}) ${firstPart}${secondPart ? `-${secondPart}` : ''}`.trim();
};

const isValidPhone = (value: string) => getPhoneInfo(value).isValid;

const getRegistrationPhoneFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const rawPhone = PHONE_QUERY_PARAMS.map((param) => params.get(param)).find(Boolean);
  if (!rawPhone) return '';

  const digits = onlyDigits(rawPhone);
  if (!digits) return '';

  const normalized = digits.startsWith('55') || digits.length > PHONE_MAX_LOCAL_LENGTH
    ? digits
    : `55${digits}`;

  return getPhoneInfo(normalized).isValid ? normalized : '';
};

const isValidHttpUrl = (value: string) => {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative inline-flex group" tabIndex={0} aria-label={text}>
    <Info className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-sminy-primary" />
    <span
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-5 z-20 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs font-normal leading-5 text-gray-700 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus:opacity-100"
    >
      {text}
    </span>
  </span>
);

const FieldLabel: React.FC<{ children: React.ReactNode; tooltip: string; required?: boolean; htmlFor?: string }> = ({
  children,
  tooltip,
  htmlFor,
  required = true
}) => (
  <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
    <span>{children}{required && <span className="text-red-500"> *</span>}</span>
    <InfoTooltip text={tooltip} />
  </label>
);

// Configuração Padrão do Fluxo (Baseada na Clínica 2 - Dental Clinic Flow)
const DEFAULT_CONVERSATION_FLOW = {
  step0: "[Adapte essa mensagem de acordo com o contexto da mensagem recebida do usuário] Olá! Me chamo [nome-assistente] e sou a consultora responsável pelo agendamento das avaliações dos tratamentos da [nome-da-clínica]. Nossa equipe é completa e está pronta para oferecer os melhores tratamentos.",
  step1First: "Prazer em conhecê-lo! Aqui na [nome-da-clinica] oferecemos uma variedade de tratamentos, como [lista de tratamentos oferecidos] e muito mais. Está sentindo algum desconforto ou dor nos dentes?",
  step1Second: "A [nome_da_clinica] é pioneira no Brasil no tratamento de [sugira um tratamento para a dor do paciente]. [Pergunte qual o tratamento de interese de forma contextualizada, se é o tratamento sugerido que interessa]",
  step2: "Antes de seguirmos, me conta: é a sua primeira vez aqui na clínica ou você já é nosso paciente?",
  step3: "[escreva esse texto corrido sem quebra de linha e bullets points, essa pergunta deve ser geradas com mais de 150 tokens pra envio por audio de forma natural] Entendo perfeitamente sua situação! Sei como [dor] pode ser frustrante e impactar sua qualidade de vida, autoestima e bem-estar no dia a dia. Você não merece conviver com esse desconforto por mais tempo. A boa notícia é que o [tratamento] vai resolver definitivamente seu problema! Por que nossa solução é diferente: Alívio imediato - Você sairá da primeira consulta já com um plano claro de tratamento. Sem dor e sem medo - Utilizamos técnicas modernas que garantem máximo conforto. Resultados duradouros - Não é só um \"tampão\", é uma solução definitiva. Equipe experiente - Mais de [X anos] transformando sorrisos e devolvendo confiança. Tecnologia exclusiva - Equipamentos de última geração para precisão cirúrgica. O que nossos pacientes falam: \"Finalmente posso sorrir sem vergonha!\" \"Não sinto mais dor nenhuma, mudou minha vida!\" Pare de sofrer desnecessariamente! Cada dia que você adia esse tratamento é mais um dia convivendo com desconforto. Nossa agenda costuma lotar rapidamente, mas posso reservar uma vaga especial para você ainda esta semana. Posso verificar nossa agenda e agendar sua avaliação? ",
  max_tokens: 300,
  financial_redirect: { type: "fixo", number: "" },
  regular_redirect: { type: "fixo", number: "" },
  maintenance_redirect: { type: "fixo", number: "" },
  active_patients_redirect: { type: "fixo", number: "" },
  few_shots: [] // Clear few_shots as requested for default flow
};

const RegisterCompanyApproval = () => {
  const { isDark } = useTheme();
  const [draftScope] = useState(() => getRegistrationPhoneFromUrl());
  const [restoredDraft] = useState(() => readRegistrationDraft(draftScope));
  const [draftSaveMessage, setDraftSaveMessage] = useState('');
  // Guard to prevent double-click on "Next" triggering "Submit" immediately
  const [canSubmit, setCanSubmit] = useState(true);

  // Estados de navegação
  const [currentStep, setCurrentStep] = useState<Step>(restoredDraft.currentStep ?? 1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const navigate = useNavigate();

  // ========== Step 1: Dados da Conta ==========
  const [fullName, setFullName] = useState(restoredDraft.fullName ?? '');
  const [email, setEmail] = useState(restoredDraft.email ?? '');
  const [clinicName, setClinicName] = useState(restoredDraft.clinicName ?? ''); // Razão social informada para conferência
  const [clinicCnpj, setClinicCnpj] = useState(restoredDraft.clinicCnpj ?? ''); // CNPJ numérico ou alfanumérico
  const [adminPhone, setAdminPhone] = useState(restoredDraft.adminPhone ?? ''); // Novo: WhatsApp Admin

  // ========== Step 2: Informações da Clínica ==========
  const [clinicDisplayName, setClinicDisplayName] = useState(restoredDraft.clinicDisplayName ?? ''); // Nome Fantasia
  const [clinicLocation, setClinicLocation] = useState(restoredDraft.clinicLocation ?? '');
  const [clinicAddress, setClinicAddress] = useState(restoredDraft.clinicAddress ?? '');
  const [clinicWhatsApp, setClinicWhatsApp] = useState(restoredDraft.clinicWhatsApp ?? '');
  const [clinicLandline, setClinicLandline] = useState(restoredDraft.clinicLandline ?? ''); // Novo Telefone Fixo
  const [clinicInstagram, setClinicInstagram] = useState(restoredDraft.clinicInstagram ?? '');
  const [clinicFacebook, setClinicFacebook] = useState(restoredDraft.clinicFacebook ?? '');
  const [clinicSite, setClinicSite] = useState(restoredDraft.clinicSite ?? '');
  const [googleMapsLink, setGoogleMapsLink] = useState(restoredDraft.googleMapsLink ?? ''); // Novo Google Maps
  const [clinicHistory, setClinicHistory] = useState(restoredDraft.clinicHistory ?? '');
  const [clinicLogo, setClinicLogo] = useState<string | null>(restoredDraft.clinicLogo ?? null);
  const [adminPhoneLockedFromUrl, setAdminPhoneLockedFromUrl] = useState(false);

  // REDIRECTS (Step 2)
  const [financialRedirectNumber, setFinancialRedirectNumber] = useState(restoredDraft.financialRedirectNumber ?? '');
  const [regularRedirectNumber, setRegularRedirectNumber] = useState(restoredDraft.regularRedirectNumber ?? '');
  const [maintenanceRedirectNumber, setMaintenanceRedirectNumber] = useState(restoredDraft.maintenanceRedirectNumber ?? '');
  const [activePatientsRedirectNumber, setActivePatientsRedirectNumber] = useState(restoredDraft.activePatientsRedirectNumber ?? '');

  // ========== Step 3: Identidade do Agente ==========
  const [assistantName, setAssistantName] = useState(restoredDraft.assistantName ?? '');
  const [assistantRole, setAssistantRole] = useState(restoredDraft.assistantRole ?? '');
  const assistantResponsibility = 'Agendar consultas de avaliação'; // TRAVADO
  const [assistantFormality, setAssistantFormality] = useState(restoredDraft.assistantFormality ?? 'formal');
  const [assistantTone, setAssistantTone] = useState(restoredDraft.assistantTone ?? 'amigável');
  const [assistantLanguage, setAssistantLanguage] = useState(restoredDraft.assistantLanguage ?? 'pt-BR');

  // ========== Step 4: Equipe e Especialidades ==========
  const [technicalResponsible, setTechnicalResponsible] = useState(restoredDraft.technicalResponsible ?? '');
  const [treatments, setTreatments] = useState<Treatment[]>(restoredDraft.treatments ?? []);

  // ========== Step 5: Configuração de Agendamento ==========
  const initialDayConfig = {
    open: false,
    morningEnabled: false,
    morningStart: '',
    morningEnd: '',
    afternoonEnabled: false,
    afternoonStart: '',
    afternoonEnd: '',
  };
  const [monday, setMonday] = useState<DayConfig>(restoredDraft.monday ?? initialDayConfig);
  const [tuesday, setTuesday] = useState<DayConfig>(restoredDraft.tuesday ?? initialDayConfig);
  const [wednesday, setWednesday] = useState<DayConfig>(restoredDraft.wednesday ?? initialDayConfig);
  const [thursday, setThursday] = useState<DayConfig>(restoredDraft.thursday ?? initialDayConfig);
  const [friday, setFriday] = useState<DayConfig>(restoredDraft.friday ?? initialDayConfig);
  const [saturday, setSaturday] = useState<DayConfig>(restoredDraft.saturday ?? initialDayConfig);
  const [sunday, setSunday] = useState<DayConfig>(restoredDraft.sunday ?? initialDayConfig);
  const [consultationDuration, setConsultationDuration] = useState(restoredDraft.consultationDuration ?? 30);
  const numberOfSuggestions = 2; // TRAVADO em 2

  // ========== Step 6: Informações Financeiras ==========
  const [acceptsHealthInsurance, setAcceptsHealthInsurance] = useState(restoredDraft.acceptsHealthInsurance ?? false);
  const [healthInsurancePlans, setHealthInsurancePlans] = useState(restoredDraft.healthInsurancePlans ?? '');
  const [paymentMethods, setPaymentMethods] = useState(restoredDraft.paymentMethods ?? '');
  const [installmentConditions, setInstallmentConditions] = useState(restoredDraft.installmentConditions ?? '');
  const [evaluationPrice, setEvaluationPrice] = useState(restoredDraft.evaluationPrice ?? '');
  const [treatmentPrices, setTreatmentPrices] = useState(restoredDraft.treatmentPrices ?? '');
  const [pricesUnderEvaluation, setPricesUnderEvaluation] = useState(restoredDraft.pricesUnderEvaluation ?? false); // Novo Checkbox

  const draftData: RegistrationDraftData = {
    fullName, email, clinicName, clinicCnpj, adminPhone, clinicDisplayName, clinicLocation,
    clinicAddress, clinicWhatsApp, clinicLandline, clinicInstagram, clinicFacebook, clinicSite,
    googleMapsLink, clinicHistory, financialRedirectNumber, regularRedirectNumber,
    maintenanceRedirectNumber, activePatientsRedirectNumber, assistantName, assistantRole,
    assistantFormality, assistantTone, assistantLanguage, technicalResponsible, healthInsurancePlans,
    paymentMethods, installmentConditions, evaluationPrice, treatmentPrices, currentStep, personType: 'pj',
    clinicLogo, treatments, monday, tuesday, wednesday, thursday, friday, saturday, sunday,
    consultationDuration, acceptsHealthInsurance, pricesUnderEvaluation,
  };
  const draftSnapshot = JSON.stringify(draftData);
  useEffect(() => {
    if (registrationComplete) return;
    const saved = writeRegistrationDraft(draftScope, JSON.parse(draftSnapshot));
    setDraftSaveMessage(saved
      ? ''
      : 'Não foi possível salvar neste navegador. Mantenha esta página aberta para continuar.');
  }, [draftSnapshot, draftScope, registrationComplete]);

  // ========== Métodos Auxiliares ==========
  useEffect(() => {
    const phoneFromUrl = getRegistrationPhoneFromUrl();
    if (!phoneFromUrl) return;

    setAdminPhone(formatPhone(phoneFromUrl));
    setAdminPhoneLockedFromUrl(true);
  }, []);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('O logo deve ter no máximo 2MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setClinicLogo(reader.result as string);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => setClinicLogo(null);

  // ========== Validações ==========
  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateStep1 = (): boolean => {
    if (!fullName.trim()) { setError('Por favor, insira seu nome completo'); return false; }
    if (!validateEmail(email)) { setError('Por favor, insira um email válido'); return false; }
    if (!clinicName.trim()) { setError('Por favor, insira a razão social'); return false; }
    if (!isValidCnpj(clinicCnpj)) { setError('CNPJ inválido. Confira as letras e os números informados.'); return false; }
    if (!isValidPhone(adminPhone)) { setError('Por favor, insira um WhatsApp válido para o administrador'); return false; }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!clinicDisplayName.trim()) { setError('Por favor, insira o nome fantasia da clínica'); return false; }
    if (!isValidPhone(clinicWhatsApp)) { setError('Por favor, insira um WhatsApp de suporte válido'); return false; }
    if (!clinicLocation.trim()) { setError('Por favor, insira a cidade/estado da clínica'); return false; }
    if (!clinicAddress.trim()) { setError('Por favor, insira o endereço completo da clínica'); return false; }
    if (!isValidPhone(clinicLandline)) { setError('Por favor, insira um telefone fixo válido'); return false; }
    if (!isValidPhone(financialRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento financeiro'); return false; }
    if (!isValidPhone(regularRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento geral'); return false; }
    if (!isValidPhone(maintenanceRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento de manutenção'); return false; }
    if (!isValidPhone(activePatientsRedirectNumber)) { setError('Por favor, insira um telefone válido para redirecionamento de pacientes ativos'); return false; }
    if (!isValidHttpUrl(googleMapsLink)) { setError('Por favor, insira um link válido do Google Maps começando com http:// ou https://'); return false; }
    if (!isValidHttpUrl(clinicInstagram)) { setError('Por favor, insira uma URL válida do Instagram'); return false; }
    if (!isValidHttpUrl(clinicFacebook)) { setError('Por favor, insira uma URL válida do Facebook'); return false; }
    if (!isValidHttpUrl(clinicSite)) { setError('Por favor, insira uma URL válida do site'); return false; }
    if (!clinicHistory.trim()) { setError('Por favor, conte um pouco sobre a história da clínica'); return false; }
    return true;
  };

  const validateStep3 = (): boolean => {
    if (!assistantName.trim()) { setError('Por favor, insira o nome do assistente'); return false; }
    if (!assistantRole.trim()) { setError('Por favor, insira a função/cargo do assistente'); return false; }
    if (!assistantResponsibility.trim()) { setError('Por favor, confirme a responsabilidade principal do assistente'); return false; }
    return true;
  };

  const validateStep4 = (): boolean => {
    if (!technicalResponsible.trim()) { setError('Por favor, insira o responsável técnico da clínica'); return false; }
    if (treatments.length === 0) { setError('Adicione pelo menos um tratamento oferecido'); return false; }
    const invalidTreatmentIndex = treatments.findIndex(
      (treatment) => !treatment.treatmentTitle.trim() || !treatment.description.trim()
    );
    if (invalidTreatmentIndex >= 0) {
      setError(`Preencha o nome e a descrição do tratamento #${invalidTreatmentIndex + 1}`);
      return false;
    }
    return true;
  };

  const validateDayPeriod = (
    label: string,
    periodLabel: string,
    enabled: boolean,
    start: string,
    end: string
  ) => {
    if (!enabled) return true;
    if (!start || !end) {
      setError(`Informe início e fim para ${periodLabel.toLowerCase()} de ${label}`);
      return false;
    }
    if (start >= end) {
      setError(`O horário final deve ser maior que o inicial em ${periodLabel.toLowerCase()} de ${label}`);
      return false;
    }
    return true;
  };

  const validateStep5 = (): boolean => {
    const days = [
      ['Segunda', monday],
      ['Terça', tuesday],
      ['Quarta', wednesday],
      ['Quinta', thursday],
      ['Sexta', friday],
      ['Sábado', saturday],
      ['Domingo', sunday],
    ] as const;

    const hasAnyPeriod = days.some(([, day]) => day.morningEnabled || day.afternoonEnabled);
    if (!hasAnyPeriod) { setError('Configure pelo menos um período de atendimento na semana'); return false; }

    for (const [label, day] of days) {
      if (!validateDayPeriod(label, 'Manhã', day.morningEnabled, day.morningStart, day.morningEnd)) return false;
      if (!validateDayPeriod(label, 'Tarde', day.afternoonEnabled, day.afternoonStart, day.afternoonEnd)) return false;
    }

    if (!consultationDuration || consultationDuration < 1) { setError('Informe uma duração válida para as consultas'); return false; }
    return true;
  };

  const validateStep6 = (): boolean => {
    if (acceptsHealthInsurance && !healthInsurancePlans.trim()) { setError('Informe os convênios aceitos ou desmarque a opção de convênios'); return false; }
    if (!paymentMethods.trim()) { setError('Informe as formas de pagamento'); return false; }
    if (!installmentConditions.trim()) { setError('Informe as condições de parcelamento'); return false; }
    if (!evaluationPrice.trim()) { setError('Informe o preço da avaliação ou marque avaliação gratuita'); return false; }
    if (!pricesUnderEvaluation && !treatmentPrices.trim()) { setError('Informe a tabela de preços dos tratamentos ou marque valores somente após avaliação'); return false; }
    return true;
  };

  const validateCurrentStep = () => {
    if (currentStep === 1) return validateStep1();
    if (currentStep === 2) return validateStep2();
    if (currentStep === 3) return validateStep3();
    if (currentStep === 4) return validateStep4();
    if (currentStep === 5) return validateStep5();
    if (currentStep === 6) return validateStep6();
    return true;
  };

  // ========== Navegação ==========
  const handleNext = () => {
    setError('');
    if (!validateCurrentStep()) {
      window.scrollTo(0, 0);
      return;
    }
    window.scrollTo(0, 0);
    if (currentStep < 5) setCurrentStep((prev) => (prev + 1) as Step);
    else if (currentStep === 5) {
      // Cooldown before allowing submission to prevent accidental double-clicks
      setCanSubmit(false);
      setCurrentStep(6);
      setTimeout(() => setCanSubmit(true), 1000);
    }
  };

  const handleBack = () => {
    setError('');
    window.scrollTo(0, 0);
    if (currentStep > 1) setCurrentStep((prev) => (prev - 1) as Step);
  };

  // ========== Submit Final ==========
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    // Prevent premature submission on Enter key if not on the final step
    if (currentStep < 6) {
      handleNext();
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const validators = [validateStep1, validateStep2, validateStep3, validateStep4, validateStep5, validateStep6];
      for (let index = 0; index < validators.length; index += 1) {
        if (!validators[index]()) {
          setCurrentStep((index + 1) as Step);
          setIsLoading(false);
          window.scrollTo(0, 0);
          return;
        }
      }

      try {
        const policy = await api.get('/auth/registration-policy', { timeout: 8000 });
        if (policy.status !== 200 || policy.data?.contract !== 'company-registration-approval-v1' ||
            policy.data?.requires_approval !== true || policy.data?.company_only !== true) {
          setError(REGISTRATION_POLICY_UNAVAILABLE_MESSAGE);
          return;
        }
      } catch {
        setError(REGISTRATION_POLICY_UNAVAILABLE_MESSAGE);
        return;
      }

      const cleanDocument = normalizeCnpj(clinicCnpj);
      const autoPassword = generateRegistrationPassword();
      const cleanPhone = clinicWhatsApp.replace(/\D/g, '');
      const cleanAdminPhone = adminPhone.replace(/\D/g, '');

      // Helper para formatar telefone para redirecionamento (apenas números)
      const cleanRedir = (val: string) => val.replace(/\D/g, '');

      // Prepare Default Redirects
      const finalFlow = {
        ...DEFAULT_CONVERSATION_FLOW,
        financial_redirect: { type: "fixo", number: cleanRedir(financialRedirectNumber) || cleanPhone },
        regular_redirect: { type: "fixo", number: cleanRedir(regularRedirectNumber) || cleanPhone },
        maintenance_redirect: { type: "fixo", number: cleanRedir(maintenanceRedirectNumber) || cleanPhone },
        active_patients_redirect: { type: "fixo", number: cleanRedir(activePatientsRedirectNumber) || cleanPhone }
      };

      const registerPayload = {
        email,
        password: autoPassword,
        full_name: fullName,
        registration_source: adminPhoneLockedFromUrl ? 'whatsapp_link' : 'direct_register',
        registration_context: {
          admin_phone_prefilled: adminPhoneLockedFromUrl
        },
        clinic: {
          cnpj: cleanDocument,
          cpf_cnpj: cleanDocument,
          person_type: 'pj',
          razao_social: clinicName,
          business_type_id: 1,
        },
        clinic_info: {
          clinic_name: clinicDisplayName,
          clinic_location: clinicLocation,
          clinic_address: clinicAddress,
          clinic_landline: clinicLandline, // Fixed: matches backend ClinicInfoData
          clinic_whatsapp: cleanPhone,
          admin_phone: cleanAdminPhone,
          clinic_instagram: clinicInstagram,
          clinic_facebook: clinicFacebook,
          clinic_site: clinicSite,
          google_maps_link: googleMapsLink, // Fixed: matches backend ClinicInfoData
          clinic_history: clinicHistory,
          clinic_logo: clinicLogo,
        },
        agent_config: {
          // Assistant Identity
          assistant_name: assistantName,
          assistant_role: assistantRole,
          assistant_responsibility: assistantResponsibility,
          assistant_formality: assistantFormality,
          assistant_tone: assistantTone,
          assistant_language: assistantLanguage,

          assistant_identity: {
            assistant_name: assistantName,
            assistant_role: assistantRole,
            assistant_responsibility: assistantResponsibility,
            assistant_formality: assistantFormality,
            assistant_tone: assistantTone,
            assistant_language: assistantLanguage
          },

          team_and_specialties: {
            technical_responsible: technicalResponsible,
            treatments: treatments
          },

          scheduling_config: {
            monday, tuesday, wednesday, thursday, friday, saturday, sunday,
            consultation_duration: consultationDuration,
            number_of_suggestions: numberOfSuggestions
          },

          financial_config: {
            accepts_health_insurance: acceptsHealthInsurance,
            health_insurance_plans: healthInsurancePlans,
            payment_methods: paymentMethods.split(',').map(s => s.trim()).filter(Boolean),
            installment_conditions: installmentConditions,
            evaluation_price: evaluationPrice,
            treatment_prices: treatmentPrices,
            prices_under_evaluation: pricesUnderEvaluation // New field
          },

          conversation_flow: finalFlow
        }
      };
      const response = await api.post('/auth/register', registerPayload);
      if (response.data?.status !== 'pending') {
        throw new Error('Não foi possível confirmar o envio do cadastro. Tente novamente mais tarde.');
      }
      clearRegistrationDraft();

      setRegistrationComplete(true);

    } catch (err: any) {
      console.error(
        'Erro no registro:',
        err?.response?.status ?? err?.name ?? 'erro desconhecido',
      );
      const localGenerationFailed = err?.message === SECURE_PASSWORD_GENERATION_ERROR;
      setError(
        localGenerationFailed
          ? 'Não foi possível gerar uma senha segura neste navegador. Atualize a página ou tente outro navegador.'
          : typeof err?.response?.data?.detail === 'string'
            ? err.response.data.detail
            : err?.response?.data?.detail?.message || 'Não foi possível enviar o cadastro. Confira seus dados e tente novamente.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalize = (e: React.MouseEvent) => {
    // Strict isolation: This function is ONLY called by clicking the button.
    // It is separate from the form's implicit submit.
    handleSubmit(e as any);
  };

  const steps = ['Conta', 'Clínica', 'Assistente', 'Equipe', 'Agendamento', 'Financeiro'];
  const stepTitles = { 1: 'Vamos começar', 2: 'Clínica', 3: 'Assistente', 4: 'Equipe e especialidades', 5: 'Agendamento', 6: 'Financeiro' };
  const stepDescriptions = {
    1: 'Informe o responsável e o CNPJ ativo da empresa para análise.',
    2: 'Organize os dados que seus pacientes vão precisar.',
    3: 'Defina como seu assistente se apresenta e conversa.',
    4: 'Apresente os profissionais e tratamentos da clínica.',
    5: 'Escolha os dias e horários disponíveis para avaliações.',
    6: 'Informe as condições que o assistente poderá apresentar.'
  };
  const StepIndicator = () => (
    <nav
      aria-label="Etapas do cadastro"
      className="register-progress"
      style={{ ['--register-step-index' as string]: String(currentStep) }}
    >
      <p className="register-mobile-progress">Etapa {currentStep} de 6 · {steps[currentStep - 1]}</p>
      <ol>{steps.map((label, index) => (
        <li key={label} aria-current={currentStep === index + 1 ? 'step' : undefined}
          className={currentStep > index + 1 ? 'is-complete' : ''}>
          <span className="register-step-number" aria-hidden="true">{currentStep > index + 1 ? <Check size={16} /> : index + 1}</span>
          <span className="register-step-label">{label}</span>
          <span className="sr-only">{currentStep > index + 1 ? 'concluída' : ''}</span>
        </li>
      ))}</ol>
    </nav>
  );

  // Tela de sucesso após registro
  if (registrationComplete) {
    return (
      <div data-theme={isDark ? 'dark' : 'light'} className="register-page register-success min-h-screen w-full flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="register-card p-6 md:p-10 text-center">
            {/* Ícone de Sucesso */}
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-10 h-10 text-green-600" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Cadastro enviado para análise
            </h1>
            <p className="text-gray-600 mb-6">
              Recebemos os dados da sua empresa. A equipe da Sminy vai conferir o cadastro antes de liberar o acesso.
            </p>

            {/* Próximos Passos */}
            <div className="bg-sminy-blue-light rounded-xl p-4 mb-6 text-left border border-sminy-primary/20">
              <h3 className="text-sm font-semibold text-sminy-primary mb-3">
                Próximos passos
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-sminy-primary mt-0.5">•</span>
                  <span>Aguarde a <strong>aprovação administrativa</strong> do cadastro.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sminy-primary mt-0.5">•</span>
                  <span>O acesso à plataforma e a configuração do atendimento dependem dessa aprovação.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sminy-primary mt-0.5">•</span>
                  <span>Nenhuma cobrança será criada durante a análise. Seus dados de contato serão usados na conferência do cadastro.</span>
                </li>
              </ul>
            </div>

            {/* Botão de Login */}
            <button
              onClick={() => navigate('/')}
              className="w-full h-12 bg-sminy-primary hover:bg-sminy-primary-hover text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2"
            >
              Voltar ao início
              <ChevronRight className="w-4 h-4" />
            </button>

            <p className="text-xs text-gray-500 mt-4">
              Em caso de dúvidas, entre em contato pelo suporte.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="register-page">
      <header className="register-header">
        <a href="/" aria-label="Sminy.ai — início" className="register-brand">Sminy.ai</a>
        <p>Já tem conta? <a href="/">Fazer login</a></p>
      </header>
      <div className="register-layout">
        <aside className="register-sidebar">
          <div className="register-sidebar-intro"><h2>Sua clínica na Sminy</h2><p>Um passo de cada vez para preparar o seu atendimento.</p></div>
          <StepIndicator />
          <p className="register-sidebar-note">Seus dados ajudam a configurar um atendimento com a sua identidade.</p>
        </aside>
        <main className="register-card">
          <div className="register-title" aria-live="polite">
            <h1>{stepTitles[currentStep]}</h1>
            <p>{stepDescriptions[currentStep]}</p>
            {draftSaveMessage ? (
              <p className="register-draft-status" role="status">{draftSaveMessage}</p>
            ) : null}
          </div>

          {error && <div role="alert" className="mb-4 p-4 rounded-xl bg-red-50 text-red-600 text-sm border border-red-200">{error}</div>}
          <form aria-label={stepTitles[currentStep]} onSubmit={(e) => e.preventDefault()}>
            {/* STEP 1: DADOS DA CONTA */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <h2 className="register-section-title">Seu acesso</h2>
                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-1" tooltip="Nome da pessoa responsável pelo cadastro e pela conta principal.">Nome Completo</FieldLabel>
                  <input id="register-field-1" type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)}
                    className="flex h-11 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm focus:ring-2 focus:ring-sminy-primary/50 focus:border-sminy-primary outline-none transition-all"
                    placeholder="Seu nome completo" />
                </div>
                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-2" tooltip="Email usado para login, cobranças e comunicação de suporte.">Email</FieldLabel>
                  <input id="register-field-2" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="flex h-11 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm focus:ring-2 focus:ring-sminy-primary/50 focus:border-sminy-primary outline-none transition-all"
                    placeholder="seu@email.com" />
                </div>
                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-3" tooltip="WhatsApp do responsável para contato durante a análise do cadastro e a configuração após aprovação.">WhatsApp do Administrador</FieldLabel>
                  <input id="register-field-3"
                    type="tel"
                    required
                    readOnly={adminPhoneLockedFromUrl}
                    aria-readonly={adminPhoneLockedFromUrl}
                    inputMode="tel"
                    autoComplete="tel"
                    value={adminPhone}
                    onChange={(e) => setAdminPhone(formatPhone(e.target.value))}
                    className={`flex h-11 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm focus:ring-2 focus:ring-sminy-primary/50 focus:border-sminy-primary outline-none transition-all ${adminPhoneLockedFromUrl ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
                    placeholder="(99) 99999-9999 ou +55 (99) 99999-9999"
                    maxLength={19}
                  />
                  {!adminPhoneLockedFromUrl && (
                    <p className="text-xs text-gray-500">Usaremos este número para conferir o cadastro e entrar em contato após a análise.</p>
                  )}
                </div>
                <h2 className="register-section-title">Dados da empresa</h2>
                <p className="text-sm text-gray-600">O cadastro é exclusivo para empresas com CNPJ ativo e depende de aprovação da Sminy.</p>
                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-4" tooltip="Razão social da empresa. O nome oficial será confirmado na consulta ao CNPJ.">Razão Social</FieldLabel>
                  <input id="register-field-4" type="text" required value={clinicName} onChange={(e) => setClinicName(e.target.value)}
                    className="flex h-11 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm focus:ring-2 focus:ring-sminy-primary/50 focus:border-sminy-primary outline-none transition-all"
                    placeholder="Razão Social Ltda" />
                </div>
                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-5" tooltip="Validamos os dígitos, a existência e a situação ativa do CNPJ antes de receber o cadastro.">CNPJ</FieldLabel>
                  <input id="register-field-5" type="text" autoCapitalize="characters" spellCheck={false} required value={clinicCnpj} onChange={(e) => setClinicCnpj(formatCnpj(e.target.value))}
                    aria-describedby="register-cnpj-help"
                    className="flex h-11 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm focus:ring-2 focus:ring-sminy-primary/50 focus:border-sminy-primary outline-none transition-all"
                    placeholder="00.000.000/0000-00" maxLength={18} />
                  <p id="register-cnpj-help" className="text-xs text-gray-500">Aceitamos CNPJ numérico ou alfanumérico. A equipe confere também o vínculo do responsável com a empresa.</p>
                </div>
                <p className="text-xs text-gray-500">* Senha será gerada automaticamente.</p>
              </div>
            )}

            {/* STEP 2: INFO CLÍNICA */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <h2 className="register-section-title">Identificação da clínica</h2>
                <div className="space-y-2">
                  <FieldLabel htmlFor="register-logo" required={false} tooltip="Imagem usada para identificar a clínica em telas internas. Pode ser adicionada agora ou depois.">Logo da Clínica</FieldLabel>
                  <div className="flex items-center gap-4">
                    {clinicLogo ? (
                      <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200">
                        <img src={clinicLogo} alt="Logo" className="w-full h-full object-cover" />
                        <button type="button" onClick={removeLogo} aria-label="Remover logo" className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 border border-dashed border-gray-300"><ImageIcon className="w-8 h-8" /></div>
                    )}
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-sminy-primary bg-sminy-primary/10 hover:bg-sminy-primary/20 transition-colors">
                      <Upload className="w-4 h-4" /> Carregar Logo
                      <input id="register-logo" type="file" accept="image/*" className="sr-only" onChange={handleLogoUpload} />
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-6" tooltip="Nome comercial exibido para pacientes e usado na configuração do assistente.">Nome Fantasia</FieldLabel>
                  <input id="register-field-6" type="text" required value={clinicDisplayName} onChange={(e) => setClinicDisplayName(e.target.value)} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="Nome para pacientes" />
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-7" tooltip="Número para encaminhar pacientes quando o atendimento precisar sair do bot e ir para uma pessoa.">WhatsApp de Suporte Humano</FieldLabel>
                  <input id="register-field-7" type="tel" required inputMode="tel" autoComplete="tel" value={clinicWhatsApp} onChange={(e) => setClinicWhatsApp(formatPhone(e.target.value))} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="(99) 99999-9999" maxLength={19} />
                  <div className="p-3 bg-yellow-50 rounded-lg text-xs text-yellow-700 border border-yellow-200 flex gap-2">
                    <Bot className="w-4 h-4 flex-shrink-0" />
                    <p><strong>Atenção:</strong> Este NÃO é o número que será conectado no sistema (bot). Este é o número para onde o paciente será direcionado caso precise falar com um atendente humano.</p>
                  </div>
                </div>

                <h2 className="register-section-title">Localização</h2>
                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-8" tooltip="Cidade e estado usados pelo assistente para contextualizar localização e atendimento.">Cidade/Estado</FieldLabel>
                  <input id="register-field-8" type="text" required value={clinicLocation} onChange={(e) => setClinicLocation(e.target.value)} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="São Paulo, SP" />
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-9" tooltip="Endereço que o assistente poderá informar ao paciente antes da consulta.">Endereço Completo</FieldLabel>
                  <input id="register-field-9" type="text" required value={clinicAddress} onChange={(e) => setClinicAddress(e.target.value)} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <FieldLabel htmlFor="register-field-10" tooltip="Telefone fixo ou principal da clínica para referência institucional.">Telefone Fixo</FieldLabel>
                    <input id="register-field-10" type="tel" required inputMode="tel" value={clinicLandline} onChange={(e) => setClinicLandline(formatPhone(e.target.value))} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="(99) 9999-9999" maxLength={19} />
                  </div>
                </div>

                {/* Seção de Contatos de Redirecionamento */}
                <div className="pt-4 pb-2 border-t border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Contatos para Redirecionamento</h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <FieldLabel htmlFor="register-field-11" tooltip="Número para encaminhar dúvidas de cobrança, pagamento e financeiro.">Financeiro</FieldLabel>
                        <input id="register-field-11" type="tel" required inputMode="tel" value={financialRedirectNumber} onChange={(e) => setFinancialRedirectNumber(formatPhone(e.target.value))} className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="(99) 99999-9999" maxLength={19} />
                      </div>
                      <div className="space-y-1">
                        <FieldLabel htmlFor="register-field-12" tooltip="Número padrão para encaminhamentos gerais quando o bot não deve continuar sozinho.">Regular (Geral)</FieldLabel>
                        <input id="register-field-12" type="tel" required inputMode="tel" value={regularRedirectNumber} onChange={(e) => setRegularRedirectNumber(formatPhone(e.target.value))} className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="(99) 99999-9999" maxLength={19} />
                      </div>
                      <div className="space-y-1">
                        <FieldLabel htmlFor="register-field-13" tooltip="Número para pacientes que já estão em manutenção ou acompanhamento.">Manutenção</FieldLabel>
                        <input id="register-field-13" type="tel" required inputMode="tel" value={maintenanceRedirectNumber} onChange={(e) => setMaintenanceRedirectNumber(formatPhone(e.target.value))} className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="(99) 99999-9999" maxLength={19} />
                      </div>
                      <div className="space-y-1">
                        <FieldLabel htmlFor="register-field-14" tooltip="Número para encaminhar pacientes que já estão em tratamento ativo.">Pacientes Ativos</FieldLabel>
                        <input id="register-field-14" type="tel" required inputMode="tel" value={activePatientsRedirectNumber} onChange={(e) => setActivePatientsRedirectNumber(formatPhone(e.target.value))} className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="(99) 99999-9999" maxLength={19} />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">Todos os redirecionamentos são obrigatórios para evitar encaminhamento errado no atendimento.</p>
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-sminy-primary" /> Presença Digital
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <FieldLabel htmlFor="register-field-15" tooltip="Link que ajuda o paciente a localizar a clínica com precisão.">Link do Google Maps</FieldLabel>
                      <input id="register-field-15" type="url" required value={googleMapsLink} onChange={(e) => setGoogleMapsLink(e.target.value)} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="https://maps.app.goo.gl/..." />
                    </div>
                    <div className="space-y-2">
                      <FieldLabel htmlFor="register-field-16" tooltip="Perfil usado pelo assistente como referência de presença digital da clínica.">Instagram (URL)</FieldLabel>
                      <input id="register-field-16" type="url" required value={clinicInstagram} onChange={(e) => setClinicInstagram(e.target.value)} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="https://instagram.com/..." />
                    </div>
                    <div className="space-y-2">
                      <FieldLabel htmlFor="register-field-17" tooltip="Página institucional para consulta e referência da clínica.">Facebook (URL)</FieldLabel>
                      <input id="register-field-17" type="url" required value={clinicFacebook} onChange={(e) => setClinicFacebook(e.target.value)} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="https://facebook.com/..." />
                    </div>
                    <div className="space-y-2">
                      <FieldLabel htmlFor="register-field-18" tooltip="Site oficial para referência do assistente e da equipe.">Site (URL)</FieldLabel>
                      <input id="register-field-18" type="url" required value={clinicSite} onChange={(e) => setClinicSite(e.target.value)} className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none" placeholder="https://site.com.br" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="register-field-19" tooltip="Resumo usado para o assistente falar da clínica com contexto e consistência."><span className="inline-flex items-center gap-2"><FileText className="w-4 h-4 text-sminy-primary" /> História da clínica</span></FieldLabel>
                  <textarea id="register-field-19"
                    value={clinicHistory}
                    onChange={(e) => setClinicHistory(e.target.value)}
                    rows={3}
                    required
                    className="w-full rounded-xl border border-gray-300 p-4 text-sm focus:ring-2 focus:ring-sminy-primary/50 outline-none resize-none"
                    placeholder="Conte um pouco sobre a história da sua clínica..."
                  />
                </div>
              </div>
            )}

            {/* STEP 3: IDENTIDADE DO AGENTE */}
            {currentStep === 3 && (
              <AssistantIdentityForm persistOnChange
                assistantName={assistantName} setAssistantName={setAssistantName}
                assistantRole={assistantRole} setAssistantRole={setAssistantRole}
                assistantResponsibility={assistantResponsibility}
                setAssistantResponsibility={() => { }} // Função vazia para travar edição
                assistantFormality={assistantFormality} setAssistantFormality={setAssistantFormality}
                assistantTone={assistantTone} setAssistantTone={setAssistantTone}
                assistantLanguage={assistantLanguage} setAssistantLanguage={setAssistantLanguage}
                disabledResponsibility={true} // Ativa visual disabled
              />
            )}

            {/* STEP 4: EQUIPE E ESPECIALIDADES */}
            {currentStep === 4 && (
              <TeamAndSpecialtiesForm persistOnChange
                technicalResponsible={technicalResponsible}
                setTechnicalResponsible={setTechnicalResponsible}
                treatments={treatments}
                setTreatments={setTreatments}
              />
            )}

            {/* STEP 5: AGENDAMENTO */}
            {currentStep === 5 && (
              <SchedulingConfigForm persistOnChange
                monday={monday} setMonday={setMonday}
                tuesday={tuesday} setTuesday={setTuesday}
                wednesday={wednesday} setWednesday={setWednesday}
                thursday={thursday} setThursday={setThursday}
                friday={friday} setFriday={setFriday}
                saturday={saturday} setSaturday={setSaturday}
                sunday={sunday} setSunday={setSunday}
                consultationDuration={consultationDuration}
                setConsultationDuration={setConsultationDuration}
                numberOfSuggestions={2} // Valor fixo
                setNumberOfSuggestions={() => { }} // Função vazia
                disabledSuggestions={true} // Ativa visual disabled
              />
            )}

            {/* STEP 6: FINANCEIRO */}
            {currentStep === 6 && (
              <div className="space-y-4">
                <FinancialConfigForm persistOnChange
                  acceptsHealthInsurance={acceptsHealthInsurance}
                  setAcceptsHealthInsurance={setAcceptsHealthInsurance}
                  healthInsurancePlans={healthInsurancePlans}
                  setHealthInsurancePlans={setHealthInsurancePlans}
                  paymentMethods={paymentMethods}
                  setPaymentMethods={setPaymentMethods}
                  installmentConditions={installmentConditions}
                  setInstallmentConditions={setInstallmentConditions}
                  evaluationPrice={evaluationPrice}
                  setEvaluationPrice={setEvaluationPrice}
                  treatmentPrices={treatmentPrices}
                  setTreatmentPrices={setTreatmentPrices}
                  pricesUnderEvaluation={pricesUnderEvaluation}
                  setPricesUnderEvaluation={setPricesUnderEvaluation}
                />
              </div>
            )}

            <div className="register-actions flex gap-3 mt-8">
              {currentStep > 1 && (
                <button type="button" onClick={handleBack} disabled={isLoading} className="flex-1 h-11 px-4 py-2 rounded-xl text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 flex items-center justify-center gap-2">
                  <ChevronLeft className="w-4 h-4" /> Voltar
                </button>
              )}
              {currentStep < 6 ? (
                <button type="button" onClick={handleNext} disabled={isLoading} className="flex-1 h-11 px-4 py-2 rounded-xl text-sm font-medium bg-sminy-primary text-white hover:bg-sminy-primary-hover flex items-center justify-center gap-2">
                  Continuar <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleFinalize}
                  disabled={isLoading || !canSubmit}
                  className={`flex-1 h-11 px-4 py-2 rounded-xl text-sm font-medium flex items-center justify-center gap-2 text-white transition-all
                    ${isLoading || !canSubmit ? 'bg-gray-400 cursor-not-allowed' : 'bg-sminy-primary hover:bg-sminy-primary-hover'}
                  `}
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {isLoading ? 'Enviando para análise...' : canSubmit ? 'Finalizar cadastro' : 'Aguarde...'}
                </button>
              )}
            </div>
          </form>
          <div className="mt-6 text-center text-sm">
            <span className="text-gray-500">Já é membro? </span>
            <a href="/" className="text-sminy-primary font-medium hover:underline">Faça login</a>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RegisterCompanyApproval;
